import { MemberIndex, MemberIndexEntry, MemberRow, memberPathKey, rowUid } from 'src/components/star-grid/utils/subset-editor/memberSubset';
import { PnlSubsetViewEntry } from 'src/components/star-grid/starGridTypes';
import { SubsetEntry } from 'src/components/star-grid/utils/subset-editor/memberSubsetEntries';

/**
 * One stored row of a saved view — the contract shared by the Prophecy import and Save As.
 *
 * `path` is WHICH POSITION the row was (root-first member ids); `expanded` whether it was open. Both are
 * optional: the Prophecy export records neither, so an imported view is positionless and collapsed, while a
 * saved view carries both and restores exactly what its author saw.
 */
export interface SavedViewEntry {
  readonly id: string;
  readonly depth: number;
  readonly path?: readonly string[];
  readonly expanded?: boolean;
}

/**
 * Member id -> the index entry to describe it by when a saved view names no position.
 *
 * A member sits at one position per parent. Label and leafness are identical at all of them, so the only
 * real choice is `weight` and the position key itself; the FIRST in hierarchy order is taken, which is the
 * one the tree shows first.
 *
 * Keyed lowercase: view entries carry Prophecy's element case, tree ids the closure's ancestor spelling.
 */
const indexByMemberId = (index: MemberIndex): Map<string, MemberIndexEntry> => {
  const byId = new Map<string, MemberIndexEntry>();
  for (const entry of index.values()) {
    const key = entry.id.toLowerCase();
    const existing = byId.get(key);
    if (existing === undefined || entry.order < existing.order) byId.set(key, entry);
  }
  return byId;
};

/**
 * A stored row whose member is not in this hierarchy's tree, described from the view alone.
 *
 * Kept, NOT dropped. Dropping them silently shrank a view — the Leaves export rendered 1,163 of its 2,763
 * rows — and a saved view's membership is the view's own claim, not something the tree gets a veto over. A
 * member can be missing because it sits in another of the dimension's hierarchies, because the export
 * predates a re-export, or because the closure carries no parent edge for it.
 *
 * Described as little as honestly possible: the id is the only label we have, `weight` is BLANK rather than
 * a guessed 0, and the key is a ONE-SEGMENT position key. That last part cannot collide with a real
 * position — a root by that name would have been found by the lookup — so the row simply has no children to
 * drill into, which is the truth.
 */
const absentPosition = (row: SavedViewEntry): MemberIndexEntry => ({
  key: memberPathKey([row.id]),
  id: row.id,
  label: row.id,
  isLeaf: true,
  depth: 0,
  path: [row.id],
  childKeys: [],
  weight: null,
  order: 0
});

/** The index entry for a stored row: its recorded position when it has one, else a representative. */
const positionFor = (row: SavedViewEntry, index: MemberIndex, byId: Map<string, MemberIndexEntry>): MemberIndexEntry | undefined => {
  if (row.path !== undefined && row.path.length > 0) {
    const exact = index.get(memberPathKey([...row.path]));
    if (exact !== undefined) return exact;
  }
  return byId.get(row.id.toLowerCase());
};

/**
 * A saved view's stored rows -> the editor's ordered entries.
 *
 * The INVERSE of `entryRows`, which derives depth from order plus a `parentUid` link: here depth is given
 * and the link is reconstructed, an entry at depth `d` hanging off the nearest shallower row still open.
 *
 * `key` is the REAL position key from the index, never a synthesized one. Everything downstream keys on
 * it — `annotateDrillable` decides the ⊞ from the position's children, and drill, `Parent` and the Keep
 * family all look the position up — so a made-up key silently turns a saved view into inert text, which is
 * exactly what it did.
 *
 * `uid` stays the synthesized display chain, because it must be unique PER ROW: one member can legitimately
 * appear twice in a subset, and AG Grid drops duplicate row ids with no warning.
 *
 * Rows whose member is not in the hierarchy are still RENDERED (see `absentPosition`): a view's membership
 * is its own claim, and dropping them silently shrank it.
 */
export const viewEntries = (view: readonly SavedViewEntry[], index: MemberIndex): SubsetEntry[] => {
  const byId = indexByMemberId(index);
  const entries: SubsetEntry[] = [];
  // uid of the last row seen at each depth, so a child can find its parent.
  const openAtDepth: string[] = [];
  const chainOf = new Map<string, readonly string[]>();

  for (const row of view) {
    const position = positionFor(row, index, byId) ?? absentPosition(row);
    const storedDepth = Math.max(0, Math.floor(row.depth));
    let parentUid: string | undefined;
    for (let above = Math.min(storedDepth, openAtDepth.length) - 1; above >= 0; above -= 1) {
      if (openAtDepth[above] !== undefined) {
        parentUid = openAtDepth[above];
        break;
      }
    }
    const parentChain = parentUid === undefined ? [] : chainOf.get(parentUid) ?? [];
    const chain = [...parentChain, position.id];
    const uid = rowUid(chain);
    entries.push({
      uid,
      key: position.key,
      member: position.id,
      label: position.label,
      isLeaf: position.isLeaf,
      path: position.path,
      weight: position.weight,
      ...(parentUid === undefined ? {} : { parentUid })
    });
    chainOf.set(uid, chain);
    const depth = parentChain.length;
    openAtDepth[depth] = uid;
    // Anything deeper is no longer open — a later sibling must not adopt a stale grandchild.
    openAtDepth.length = depth + 1;
  }
  return entries;
};

/** Position keys of the rows a saved view had OPEN, for seeding the grid's expansion. */
export const viewExpandedKeys = (view: readonly SavedViewEntry[], index: MemberIndex): string[] => {
  const byId = indexByMemberId(index);
  const keys: string[] = [];
  for (const row of view) {
    if (row.expanded !== true) continue;
    // Only rows with a real position: a member absent from the tree has nothing to expand.
    const position = positionFor(row, index, byId);
    if (position !== undefined) keys.push(position.key);
  }
  return keys;
};

/**
 * The editor's rows as stored entries — what Save As sends.
 *
 * Takes the rows the GRID was given, never the searched ones: `displayRows` is post-search, so saving with
 * the search box active would silently store only the matches (a 73-row view becoming 4).
 *
 * `path` is the row's POSITION, taken from the row rather than re-derived, so a multi-parent member records
 * the parent it was actually shown under — which is what makes its edge weight reproduce on reopen.
 *
 * `expanded` is written only when true; absent means collapsed, which is the same shape an imported view
 * stores and keeps the payload small for a view of thousands of rows.
 */
/**
 * The rows a saved view should contain: those VISIBLE under the current expansion.
 *
 * Neither of the two row sets to hand is right on its own. `displayRows` is post-search, so saving with the
 * search box active would store only the matches. `gridRows` is the opposite problem — it deliberately holds
 * every row, collapsed children included, because the grid owns expand/collapse and needs them present to
 * draw an expander. Saving that stored the children of a collapsed rollup, so a view saved with
 * `Operating Income` closed reopened with its whole subtree.
 *
 * A row is visible when every ancestor above it is expanded. Expansion is keyed by POSITION, the display
 * chain by uid, so the uid→key map is what connects them.
 *
 * This is also the editor's own rule for membership — collapsing a node removes it from the report exactly as
 * it removes it from the pane — so a saved view and an OK'd selection agree on what the subset is.
 */
export const visibleForSave = (rows: readonly MemberRow[], expanded: ReadonlySet<string>): MemberRow[] => {
  const keyByUid = new Map(rows.map((row) => [row.uid, row.key]));
  return rows.filter((row) =>
    row.displayPath.slice(0, -1).every((uid) => {
      const key = keyByUid.get(uid);
      return key !== undefined && expanded.has(key);
    })
  );
};

// Returns the API's own shape (mutable `path`) rather than the reader's readonly one: this value is about to
// be serialised into a request body, not read back through `viewEntries`.
export const entriesToSave = (rows: readonly MemberRow[], expanded: ReadonlySet<string>): PnlSubsetViewEntry[] =>
  rows.map((row) => ({
    id: row.id,
    depth: row.depth,
    path: [...row.path],
    ...(expanded.has(row.key) ? { expanded: true } : {})
  }));
